import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { config } from '../config/env';
import Tabs from "../components/tabs/Tabs";
import SectionLoader from "../components/customLoader/SectionLoader";
import CardsLoader from "../components/customLoader/CardLoader";
import Section from "../components/sections/Section";
import Cards from "../components/cards/Cards";
import EmptyContent from "../components/emptycontent/EmptyContent";
import ListLoader from "../components/customLoader/ListLoader";
import Layout from "../components/layout/Layout";
import MarkdownComponent from "../components/markdowncomponent/MarkdownComponent";

const fetchTopicByID = async (id) => {
  try {
    const response = await fetch(`${config.apiBaseUrl}/api/v1/topics/${id}`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching topic by ID:', error);
    throw error;
  }
}

const fetchTopics = async () => {
  try {
    const response = await fetch(`${config.apiBaseUrl}/api/v1/topics/`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching topics:', error);
    throw error;
  }
}

const fetchResearchPapers = async ({
  page=1, 
  limit=5, 
  sort_By="published_at", 
  order_by="desc", 
  sas_token_for_first, 
  topic_id
}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    params.append('sort_by', sort_By);
    params.append('order_by', order_by);
    if(sas_token_for_first){
      params.append('sas_token_for_first', sas_token_for_first);
    }
    if(topic_id) {
      params.append('topic_id', topic_id);
    }
    const response = await fetch(`${config.apiBaseUrl}/api/v1/content/research_papers/?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching research papers:', error);
    throw error;
  }
}

const fetchContents = async ({
  page = 1,
  limit = 10,
  sort_By = "published_at",
  order_by = "desc",
  search,
  topic_id,
  content_type_ids,
  sas_token_for_first
}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    params.append('sort_by', sort_By);
    params.append('order_by', order_by);
    if (search) {
      params.append('search', search);
    }
    if (topic_id) {
      params.append('topic_id', topic_id);
    }
    if (sas_token_for_first) {
      params.append('sas_token_for_first', sas_token_for_first);
    }
    if (content_type_ids && content_type_ids.length > 0) {
      content_type_ids.forEach(id => params.append('content_type_ids', id));
    }
    const response = await fetch(`${config.apiBaseUrl}/api/v1/contents/?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching contents:', error);
    throw error;
  }
}

const fetchOverview = async () => {
  try {
    const response = await fetch(`${config.apiBaseUrl}/api/v1/overview/active`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching overview data:', error);
    throw error;
  }
}

const Home = () => {
  // DEFAULT STATE
  const defaultSelectedTab = {
    id: 0,
    name: "Overview"
  }
  const defaultTabs = [
    defaultSelectedTab
  ]
  // HOOKS
  const [selectedTab, setSelectedTab] = useState(defaultSelectedTab);
  const [topic, setTopic] = useState(null);
  const [topics, setTopics] = useState(defaultTabs)
  const [researchPapers, setResearchPapers] = useState([]);
  const [contents, setContents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [overview, setOverview] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const initialized = async () => {
      setIsLoading(true)
      const topicsData = await fetchTopics();
      setTopics([...defaultTabs, ...topicsData.data]);

      const researchPaperData = await fetchResearchPapers({sas_token_for_first: 5});
      setResearchPapers(researchPaperData.data);

      const overviewData = await fetchOverview();
      setOverview(overviewData.content);
      setIsLoading(false)
    }
    initialized()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // EVENT HANDLERS
  const handleTabChange = async ({id, name}) => {
    console.log("HANDLE TAB CHANGE", id, name)
    setResearchPapers([])
    setTopic(null)
    setSelectedTab({id, name});
    setIsLoading(true);

    try {
      let params = {sas_token_for_first: 5}
      if (id > 0) {
        const topicData = await fetchTopicByID(id);
        setTopic(topicData);
        params = {
          sas_token_for_first: 2,
          topic_id: id
        }
        const contentsData = await fetchContents({
          topic_id: id,
          content_type_ids: [1,2], // 1 = Research Paper, 2 = Article
          sas_token_for_first: 10
        });
        setContents(contentsData);
        return;
      }
      const researchPaperData = await fetchResearchPapers(params);
      setResearchPapers(researchPaperData.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }

  // RENDER FUNCTIONS
  const renderOverviewLoader = () => (
    <div>
      <SectionLoader leftSectioned={true} bgColor="bg-white" />
      <CardsLoader />
    </div>
  )

  const renderOverviewCards = () => {
    if(!researchPapers || researchPapers.length < 1)
    {
      return <EmptyContent />
    }
    return (
      <>
        <Section
          buttonEnabled={true}
          leftSectioned={true}
          bgColor={"bg-white"}
          label={researchPapers[0]?.title || ""}
          description={researchPapers[0]?.description || "No content available"}
          image={researchPapers[0]?.preview_image_url || ""}
          onClicked={() => navigate(`/researchpaper/${researchPapers[0]?.id || ""}`) }
        />
        <Cards items={researchPapers.slice(1, 5)} />
      </>
    )
  }

  const renderOverviewContent = () => {
    if(isLoading) {
      return null;
    }

    if(!overview || overview === "") {
      return null;
    }

    return (
      <MarkdownComponent markdownContent={overview} />
    )
  }
  
  const renderOverview = () => (
    <div>
      {renderOverviewContent()}
      { isLoading ? renderOverviewLoader() : renderOverviewCards() }
    </div>
  )

  const renderTopicLoader = () => (
    <div>
      <SectionLoader leftSectioned={true} bgColor="bg-white" />
      <SectionLoader leftSectioned={false} bgColor="bg-white" />
      <ListLoader />
    </div>
  )

  const renderResearchPapersByTopicID = () => {
    if (isLoading) {
      return renderTopicLoader();
    }

    if(!contents || contents.length < 1)
    {
      return <EmptyContent />
    }

    return (
      <div>
        <div>
          { contents && 
              contents.data && 
                contents.data.length > 0 && 
                  contents.data.map((item, index) => (
                    <div key={item.id}>
                      {
                        <Section
                          buttonEnabled={true}
                          leftSectioned={index % 2 === 0}
                          label={item?.title || ""}
                          tag={item?.content_type?.name || ""}
                          description={item?.description || "No content available"}
                          image={item?.preview_image_url || ""}
                          onClicked={() => {
                            switch (item.content_type.id) {
                              case 1:
                                  navigate(`/researchpaper/${item?.id || ""}`)
                                break;
                              case 2:
                                  navigate(`/blog/${item?.id || ""}`)
                                break;
                              default:
                                  console.log("No action defined for this content type");
                                break;
                            }
                          }}
                        />
                      }
                    </div>
          ))}
        </div>
      </div>
    )
  }
  
  const renderTopicByID = () => {
    return (
      <>
      { (topic && topic.content) && 
        <div>
          <MarkdownComponent
            markdownContent={topic.content}
          />
        </div>
      }
      { renderResearchPapersByTopicID() }
      </>
    )
  }

  const renderContent = () => (
    <>
      {selectedTab.id === 0 ? renderOverview() : renderTopicByID()}
    </>
  )

  // JSX
  return (
    <>
      <Helmet>
        <title>Avanade Research</title>
      </Helmet>
      <Layout>
        <Tabs
          tabChangeHandler={
            (selectedTab) => handleTabChange(selectedTab)
          }
          selectedTab={selectedTab}
          topics={topics}/>
        <div className="mt-20">
          {renderContent()}
        </div>
      </Layout> 
    </>
  )
}

export default Home;